import { create } from "zustand";
import { persist } from "zustand/middleware";
import { vehiclesApi } from "@services/api/vehicles";
import type {
  Vehicle,
  VehicleReservation,
  VehicleCategory,
  VehicleClass,
  VehicleUsage,
} from "../types/garage/vehicules";

// --- Store ---

interface VehiculesStore {
  vehicles: Vehicle[];
  reservations: VehicleReservation[];
  isLoading: boolean;
  isRefreshing: boolean;
  selectedDate: string;
  viewMode: "reservations" | "parc";
  timeView: "semaine" | "jour";
  weekViewMode: "fixe" | "dynamique";
  searchQuery: string;
  activeCategories: VehicleCategory[];
  activeUsages: VehicleUsage[];
  parcFuelTab: string;
  highlightedReservationId: string | null;
  usageFilter: "tous" | VehicleUsage;
  classFilter: "tous" | VehicleClass;
  _fetchedOrgId: string | undefined;

  fetchVehicles: (orgId: string) => Promise<void>;
  fetchReservations: (orgId: string) => Promise<void>;
  setSelectedDate: (d: string) => void;
  setHighlightedReservation: (id: string | null) => void;
  setViewMode: (m: "reservations" | "parc") => void;
  setTimeView: (t: "semaine" | "jour") => void;
  setWeekViewMode: (m: "fixe" | "dynamique") => void;
  setSearchQuery: (q: string) => void;
  setUsageFilter: (u: "tous" | VehicleUsage) => void;
  setClassFilter: (c: "tous" | VehicleClass) => void;
  toggleCategory: (c: VehicleCategory) => void;
  toggleUsage: (u: VehicleUsage) => void;
  setParcFuelTab: (tab: string) => void;
  createVehicle: (vehicle: Omit<Vehicle, 'id'>) => Promise<Vehicle>;
  updateVehicle: (id: string, partial: Partial<Vehicle>) => void;
  deleteVehicle: (id: string) => Promise<void>;
  updateReservation: (id: string, partial: Partial<VehicleReservation>) => void;
  getFilteredVehicles: () => Vehicle[];
  getWeekDays: () => { date: string; label: string; isWeekend: boolean }[];
  getReservationsForVehicle: (vehicleId: string) => VehicleReservation[];
  getReservationsForDate: (date: string) => VehicleReservation[];
}

const DAY_NAMES = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export const useVehiculesStore = create<VehiculesStore>()(
  persist(
    (set, get) => ({
  vehicles: [],
  reservations: [],
  isLoading: false,
  isRefreshing: false,
  selectedDate: todayIso(),
  viewMode: "reservations",
  timeView: "semaine",
  weekViewMode: "fixe",
  searchQuery: "",
  activeCategories: ["autre", "carrosserie", "mecanique"],
  activeUsages: ["velos", "location", "pret"],
  parcFuelTab: "tous",
  highlightedReservationId: null,
  usageFilter: "tous",
  classFilter: "tous",
  _fetchedOrgId: undefined,

  fetchVehicles: async (orgId: string) => {
    const state = get();
    if (state.isLoading || state.isRefreshing) return;
    const showLoading = state.vehicles.length === 0;
    set({ isLoading: showLoading, isRefreshing: true, _fetchedOrgId: orgId });
    try {
      const vehicles = await vehiclesApi.list({ org_id: orgId });
      set({ vehicles, isLoading: false, isRefreshing: false });
    } catch (error) {
      console.error("Error fetching vehicles:", error);
      set({ vehicles: [], isLoading: false, isRefreshing: false, _fetchedOrgId: undefined });
    }
  },

  fetchReservations: async (orgId: string) => {
    try {
      const reservations = await vehiclesApi.listReservations({ org_id: orgId });
      set({ reservations });
    } catch (error) {
      console.error("Error fetching reservations:", error);
      set({ reservations: [] });
    }
  },

  setSelectedDate: (d) => set({ selectedDate: d }),
  setViewMode: (m) => set({ viewMode: m }),
  setTimeView: (t) => set({ timeView: t }),
  setWeekViewMode: (m) => set({ weekViewMode: m }),
  setSearchQuery: (q) => set({ searchQuery: q }),
  setParcFuelTab: (tab) => set({ parcFuelTab: tab }),
  setHighlightedReservation: (id) => set({ highlightedReservationId: id }),

  createVehicle: async (vehicle) => {
    const created = await vehiclesApi.create(vehicle);
    set({ vehicles: [...get().vehicles, created] });
    return created;
  },

  updateVehicle: (id, partial) => {
    set({
      vehicles: get().vehicles.map((v) =>
        v.id === id ? { ...v, ...partial } : v,
      ),
    });
    vehiclesApi.update(id, partial).catch((e) => console.error("Error updating vehicle:", e));
  },

  deleteVehicle: async (id) => {
    await vehiclesApi.delete(id);
    set({ vehicles: get().vehicles.filter((v) => v.id !== id) });
  },

  setUsageFilter: (u) => set({ usageFilter: u }),
  setClassFilter: (c) => set({ classFilter: c }),

  toggleCategory: (c) => {
    const cats = get().activeCategories;
    if (cats.includes(c)) {
      set({ activeCategories: cats.filter((x) => x !== c) });
    } else {
      set({ activeCategories: [...cats, c] });
    }
  },

  toggleUsage: (u) => {
    const usages = get().activeUsages;
    if (usages.includes(u)) {
      set({ activeUsages: usages.filter((x) => x !== u) });
    } else {
      set({ activeUsages: [...usages, u] });
    }
  },

  updateReservation: (id, partial) => {
    set({
      reservations: get().reservations.map((r) =>
        r.id === id ? { ...r, ...partial } : r,
      ),
    });
    vehiclesApi.updateReservation(id, partial).catch((e) => console.error("Error updating reservation:", e));
  },

  getFilteredVehicles: () => {
    const { vehicles, usageFilter, classFilter, searchQuery } = get();
    const USAGE_ORDER: Record<string, number> = { location: 0, pret: 1, velos: 2 };
    return vehicles
      .filter((v) => {
        if (usageFilter !== "tous" && v.usage !== usageFilter) return false;
        if (classFilter !== "tous" && v.vehicle_class !== classFilter) return false;
        if (searchQuery) {
          const q = searchQuery.toLowerCase();
          if (
            !v.name.toLowerCase().includes(q) &&
            !v.plate.toLowerCase().includes(q)
          )
            return false;
        }
        return true;
      })
      .sort((a, b) => {
        const ua = USAGE_ORDER[a.usage] ?? 3;
        const ub = USAGE_ORDER[b.usage] ?? 3;
        if (ua !== ub) return ua - ub;
        return a.name.localeCompare(b.name, "fr");
      });
  },

  getWeekDays: () => {
    const { selectedDate, weekViewMode } = get();
    const sel = new Date(selectedDate);
    const days: { date: string; label: string; isWeekend: boolean }[] = [];

    if (weekViewMode === "fixe") {
      // Fixed: Monday to Sunday of the week containing selectedDate
      const dayOfWeek = sel.getDay(); // 0=Sun, 1=Mon, ...
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      for (let i = 0; i < 7; i++) {
        const d = new Date(sel.getFullYear(), sel.getMonth(), sel.getDate() + mondayOffset + i);
        const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        const dayName = DAY_NAMES[d.getDay()];
        const label = `${dayName} ${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
        const isWeekend = d.getDay() === 0 || d.getDay() === 6;
        days.push({ date: iso, label, isWeekend });
      }
    } else {
      // Dynamic: selected day centered with +-3 days
      for (let i = -3; i <= 3; i++) {
        const d = new Date(sel.getFullYear(), sel.getMonth(), sel.getDate() + i);
        const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        const dayName = DAY_NAMES[d.getDay()];
        const label = `${dayName} ${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
        const isWeekend = d.getDay() === 0 || d.getDay() === 6;
        days.push({ date: iso, label, isWeekend });
      }
    }
    return days;
  },

  getReservationsForVehicle: (vehicleId) => {
    return get().reservations.filter((r) => r.vehicleId === vehicleId);
  },

  getReservationsForDate: (date) => {
    return get().reservations.filter(
      (r) => r.startDate <= date && r.endDate >= date,
    );
  },
    }),
    {
      name: "vehicules-prefs",
      partialize: (state) => ({
        selectedDate: state.selectedDate,
        viewMode: state.viewMode,
        timeView: state.timeView,
        weekViewMode: state.weekViewMode,
        activeCategories: state.activeCategories,
        activeUsages: state.activeUsages,
        parcFuelTab: state.parcFuelTab,
      }),
    },
  ),
);
